import { Head, Link, router } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface Vendor {
  id: number
  display_name: string
}

interface Log {
  id: number
  log_date: string | null
  notes: string | null
  contact_name: string | null
  created_by: string | null
  created_at: string
}

interface Props {
  vendor: Vendor
  log: Log
  can_edit: boolean
}

export default function Show({ vendor, log, can_edit }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Subcontractors", href: "/company/vendors" },
    { title: vendor.display_name, href: `/company/vendors/${vendor.id}` },
    { title: "Logs", href: `/company/vendors/${vendor.id}/logs` },
    {
      title: formatDate(log.log_date) || "Log Entry",
      href: `/company/vendors/${vendor.id}/logs/${log.id}`,
    },
  ]

  const handleDelete = () => {
    if (confirm("Are you sure you want to remove this log entry?")) {
      router.delete(`/company/vendors/${vendor.id}/logs/${log.id}`)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Log Entry - ${vendor.display_name}`} />

      <div className="p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            Log Entry - {formatDateTime(log.log_date)}
          </h1>
          {can_edit && (
            <div className="space-x-2">
              <Link
                href={`/company/vendors/${vendor.id}/logs/${log.id}/edit`}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
              >
                Edit
              </Link>
              <button
                onClick={handleDelete}
                className="bg-hpi-red hover:bg-hpi-red/80 rounded px-4 py-2 text-white"
              >
                Delete
              </button>
            </div>
          )}
        </div>

        <div className="border-hpi-light-gray mb-6 rounded border p-4">
          <dl className="grid max-w-md grid-cols-2 gap-2">
            <dt className="text-hpi-med-blue font-medium">Date</dt>
            <dd>{formatDateTime(log.log_date)}</dd>
            <dt className="text-hpi-med-blue font-medium">Author</dt>
            <dd>{log.created_by ?? "-"}</dd>
            {log.contact_name && (
              <>
                <dt className="text-hpi-med-blue font-medium">Contact</dt>
                <dd>{log.contact_name}</dd>
              </>
            )}
          </dl>
        </div>

        {log.notes && (
          <div className="border-hpi-light-gray mb-6 rounded border p-4">
            <h2 className="mb-2 text-lg font-semibold">Notes</h2>
            <p className="whitespace-pre-wrap">{log.notes}</p>
          </div>
        )}

        <div className="text-hpi-med-gray text-sm">
          Created {formatDate(log.created_at)}
          {log.created_by && ` by ${log.created_by}`}
        </div>

        <div className="mt-4">
          <Link
            href={`/company/vendors/${vendor.id}/logs`}
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            &larr; Back to Logs
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return ""
  const d = new Date(dateStr)
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`
}
